import { Edit, Trash2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"

import { BreadcrumbNav } from "@/components/breadcrumb-nav"
import { LoadingState } from "@/components/loading-state"
import { PageHeader } from "@/components/page-header"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  contractsKeys,
  useContract,
} from "@/features/contracts/hooks/use-contracts"
import { useCooperatives } from "@/features/cooperatives/hooks/use-cooperatives"
import { DocumentsList } from "@/features/documents/components/documents-list"
import { OrderDeliveriesTable } from "@/features/orders/components/order-deliveries-table"
import { OrderDetailsSection } from "@/features/orders/components/order-details-section"
import { OrderStatusBadge } from "@/features/orders/components/order-status-badge"
import { OrderSummaryCards } from "@/features/orders/components/order-summary-cards"
import { useDeleteOrder } from "@/features/orders/hooks/use-order-mutations"
import { ordersKeys, useOrder } from "@/features/orders/hooks/use-orders"
import { paths } from "@/routes/paths"

export function OrderDetailPage() {
  const { orderId = "" } = useParams<{ orderId: string }>()
  const { t } = useTranslation(["orders", "contracts", "common"])
  const navigate = useNavigate()

  const [deleteOpen, setDeleteOpen] = useState(false)

  const { data: order, isLoading: loadingOrder } = useOrder(orderId)
  const contractId = order?.exporterOrderId?.id
    ? String(order.exporterOrderId?.id)
    : ""

  const { data: contract, isLoading: loadingContract } = useContract(contractId)
  const { data: cooperatives } = useCooperatives()
  const remove = useDeleteOrder(contractId)

  if (loadingOrder || loadingContract || !order || !contract) {
    return <LoadingState />
  }

  const cooperativeName =
    cooperatives?.find((c) => String(c.id) === String(order.groupId))?.name ??
    ""

  return (
    <div className="flex flex-col gap-6">
      <BreadcrumbNav
        crumbs={[
          { label: t("title", { ns: "contracts" }), to: paths.contracts },
          {
            label: contract.contractNumber ?? `#${contract.id}`,
            to: paths.contractDetail(contract.id),
          },
          { label: order.orderNumber ?? `#${order.id}` },
        ]}
      />

      <PageHeader
        title={order.orderNumber ?? `#${order.id}`}
        description={cooperativeName}
        meta={<OrderStatusBadge status={order.orderStatus} />}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate(paths.orderEdit(order.id))}
            >
              <Edit className="size-4" />
              {t("actions.edit", { ns: "common" })}
            </Button>
            <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="size-4" />
              {t("actions.delete", { ns: "common" })}
            </Button>
          </>
        }
      />

      <OrderSummaryCards order={order} contract={contract} />
      <OrderDetailsSection
        order={order}
        contract={contract}
        cooperatives={cooperatives}
      />

      <OrderDeliveriesTable order={order} />

      <DocumentsList
        documents={order.documents}
        level="order"
        contractId={contractId}
        orderId={String(order.id)}
        invalidateKeys={[
          ordersKeys.detail(String(order.id)),
          contractsKeys.detail(contractId),
        ]}
        title={t("documents.title")}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteOrder")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              {t("actions.cancel", { ns: "common" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                remove.mutate(String(order.id), {
                  onSuccess: () => {
                    setDeleteOpen(false)
                    navigate(paths.contractDetail(contract.id), {
                      replace: true,
                    })
                  },
                })
              }}
              disabled={remove.isPending}
            >
              {remove.isPending
                ? t("actions.deleting", { ns: "common" })
                : t("actions.delete", { ns: "common" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
